import { Attrs, jsonList, scalar } from '@skippay/core/utils/objectUtils';

import {
	AccountInfo,
	BillingInfo,
	CashbackInfo,
	CoinsInfo,
	Compliance,
	CurrentApplicationInfo,
	CustomerContext,
	CustomerContextDomain,
	Eligibility,
	LimitInfo,
	RevolvingInfo,
} from './types';

const eligibility = (e?: Eligibility): Attrs =>
	!e
		? {}
		: {
				...scalar('eligibility.canApplyForRevolving', e.canApplyForRevolving),
				...scalar('eligibility.canApplyForInstallments', e.canApplyForInstallments),
			};

const account = (a?: AccountInfo): Attrs => (!a ? {} : scalar('account.package', a.package));

const compliance = (c?: Compliance): Attrs =>
	jsonList('compliance.actionsRequired', c?.actionsRequired);

const limit = (l?: LimitInfo): Attrs =>
	!l
		? {}
		: {
				...scalar('limit.available', l.available),
				...scalar('limit.total', l.total),
				...scalar('limit.state', l.state),
				...scalar('limit.annualAvailable', l.annualAvailable),
				...scalar('limit.annualTotal', l.annualTotal),
				...scalar('limit.amendmentState', l.amendmentState),
				...scalar('limit.increaseOfferAvailable', l.increaseOfferAvailable),
			};

const revolving = (r?: RevolvingInfo): Attrs =>
	!r
		? {}
		: {
				...scalar('revolving.state', r.state),
				...scalar('revolving.applicationState', r.applicationState),
			};

const billing = (b?: BillingInfo): Attrs =>
	!b
		? {}
		: {
				...scalar('billing.balance', b.balance),
				...scalar('billing.total', b.total),
				...scalar('billing.minimumToPay', b.minimumToPay),
				...scalar('billing.dueDate', b.dueDate),
				...scalar('billing.daysOverdue', b.daysOverdue),
				...scalar('billing.isPaid', b.isPaid),
				...scalar('billing.canPay', b.canPay),
				...jsonList('billing.paymentMethods', b.paymentMethods),
				...scalar('billing.canDefer', b.canDefer),
			};

const card = (c?: CustomerContext['card']): Attrs =>
	!c
		? {}
		: {
				...scalar('card.state', c.state),
				...scalar('card.stateReason', c.stateReason),
			};

const coins = (c?: CoinsInfo): Attrs =>
	!c
		? {}
		: {
				...scalar('coins.balance', c.balance),
				...scalar('coins.nearestExpiration.date', c.nearestExpiration?.date),
				...scalar('coins.nearestExpiration.amount', c.nearestExpiration?.amount),
			};

const cashback = (c?: CashbackInfo): Attrs => (!c ? {} : scalar('cashback.available', c.available));

const currentApplication = (a?: CurrentApplicationInfo): Attrs =>
	!a
		? {}
		: {
				...scalar('currentApplication.eshop', a.eshop),
				...scalar('currentApplication.totalPrice', a.totalPrice),
				...jsonList('currentApplication.items', a.items),
				...scalar('currentApplication.financingType', a.financingType),
				...scalar('currentApplication.flowState', a.flowState),
				...scalar('currentApplication.rejectionCategory', a.rejectionCategory),
				...jsonList('currentApplication.installmentPlan', a.installmentPlan),
				...scalar('currentApplication.dueDate', a.dueDate),
				...scalar('currentApplication.canPayByCard', a.canPayByCard),
				...scalar(
					'currentApplication.canApplyForRevolvingWhenRejected',
					a.canApplyForRevolvingWhenRejected
				),
				...scalar('currentApplication.requiredPriceLimit', a.requiredPriceLimit),
				...scalar('currentApplication.unpaidOrdersSum', a.unpaidOrdersSum),
				...scalar('currentApplication.isAuthenticated', a.isAuthenticated),
			};

export const DOMAIN_BUILDERS: Record<CustomerContextDomain, (ctx: CustomerContext) => Attrs> = {
	eligibility: (c) => eligibility(c.eligibility),
	account: (c) => account(c.account),
	compliance: (c) => compliance(c.compliance),
	limit: (c) => limit(c.limit),
	revolving: (c) => revolving(c.revolving),
	billing: (c) => billing(c.billing),
	payInThree: (c) => jsonList('payInThree', c.payInThree),
	deferredPayments: (c) => jsonList('deferredPayments', c.deferredPayments),
	installments: (c) => jsonList('installments', c.installments),
	card: (c) => card(c.card),
	coins: (c) => coins(c.coins),
	cashback: (c) => cashback(c.cashback),
	transactions: (c) => jsonList('transactions', c.transactions),
	offers: (c) => jsonList('offers', c.offers),
	outages: (c) => jsonList('outages', c.outages),
	currentApplication: (c) => currentApplication(c.currentApplication),
};

export const buildDomainAttrs = (domain: CustomerContextDomain, ctx: CustomerContext): Attrs =>
	DOMAIN_BUILDERS[domain](ctx);

export const buildAllAttrs = (ctx: CustomerContext): Attrs =>
	(Object.keys(DOMAIN_BUILDERS) as CustomerContextDomain[]).reduce(
		(acc, domain) => ({ ...acc, ...buildDomainAttrs(domain, ctx) }),
		{} as Attrs
	);
